import { useState, useEffect } from 'react';

interface BuilderSkeletonProps {
  /** Product slug to load garment silhouette SVG. If omitted, shows a generic placeholder. */
  productSlug?: string;
  /** Which surface to show (defaults to 'front') */
  surface?: string;
}

const svgCache = new Map<string, string>();

const GarmentSilhouette = ({ productSlug, surface = 'front' }: { productSlug: string; surface: string }) => {
  const [svgContent, setSvgContent] = useState<string | null>(null);

  useEffect(() => {
    const surfaceFile = surface.replace(/([A-Z])/g, '-$1').toLowerCase();
    const src = `/products/${productSlug}/illustrations/${surfaceFile}.svg`;

    if (svgCache.has(src)) {
      setSvgContent(svgCache.get(src)!);
      return;
    }

    let cancelled = false;
    fetch(src)
      .then((r) => r.text())
      .then((text) => {
        if (cancelled) return;
        // Replace all fill colors with a uniform light gray
        const neutralized = text
          .replace(/fill="[^"]*"/g, 'fill="#e5e7eb"')
          .replace(/stroke="[^"]*"/g, 'stroke="#d1d5db"');
        svgCache.set(src, neutralized);
        setSvgContent(neutralized);
      })
      .catch(() => {
        // Silently fail — show generic placeholder instead
      });

    return () => { cancelled = true; };
  }, [productSlug, surface]);

  if (!svgContent) {
    return (
      <div className="w-full h-full flex items-center justify-center">
        <div className="w-64 h-80 rounded-2xl bg-gray-100 animate-pulse" />
      </div>
    );
  }

  return (
    <div
      className="w-full h-full flex items-center justify-center opacity-40 animate-pulse [&>svg]:max-w-full [&>svg]:max-h-full [&>svg]:w-auto [&>svg]:h-auto"
      dangerouslySetInnerHTML={{ __html: svgContent }}
    />
  );
};

export const BuilderSkeleton = ({ productSlug, surface = 'front' }: BuilderSkeletonProps) => {
  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-gray-50 overflow-hidden">
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <div className="flex-1 relative min-h-0 min-w-0 flex">
          {/* Left sidebar skeleton */}
          <div className="absolute top-4 bottom-4 left-4 z-10 flex overflow-hidden bg-white rounded-lg border border-gray-200 shadow-lg">
            {/* Icon rail */}
            <div className="w-10 flex flex-col items-center gap-3 py-4 border-r border-gray-100">
              {[1, 2, 3].map((i) => (
                <div key={i} className="w-5 h-5 rounded bg-gray-100 animate-pulse" />
              ))}
            </div>
            {/* Panel area */}
            <div className="w-52 flex flex-col p-3 gap-3">
              <div className="flex items-center justify-between">
                <div className="w-16 h-3 rounded bg-gray-100 animate-pulse" />
                <div className="w-6 h-3 rounded bg-gray-100 animate-pulse" />
              </div>
              {[1, 2, 3].map((i) => (
                <div key={i} className="flex items-center gap-2">
                  <div className="w-6 h-6 rounded bg-gray-100 animate-pulse shrink-0" />
                  <div className="flex-1 h-3 rounded bg-gray-100 animate-pulse" />
                </div>
              ))}
            </div>
          </div>

          {/* Canvas area with garment silhouette */}
          <div className="flex-1 flex items-center justify-center p-16">
            {productSlug ? (
              <GarmentSilhouette productSlug={productSlug} surface={surface} />
            ) : (
              <div className="w-96 h-[500px] rounded-2xl bg-gray-100 animate-pulse" />
            )}
          </div>

          {/* Tools bar skeleton */}
          <div className="absolute top-9 left-[284px] z-10">
            <div className="flex flex-col gap-2 bg-white rounded-lg border border-gray-200 shadow-sm p-1.5">
              {[1, 2, 3].map((i) => (
                <div key={i} className="w-6 h-6 rounded bg-gray-100 animate-pulse" />
              ))}
            </div>
          </div>

          {/* Top tabs skeleton */}
          <div className="absolute top-4 left-1/2 -translate-x-1/2 z-10">
            <div className="flex items-center gap-1 bg-white rounded-full border border-gray-200 shadow-sm px-2 py-1.5">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="w-14 h-4 rounded bg-gray-100 animate-pulse" />
              ))}
            </div>
          </div>

          {/* Right panel skeleton */}
          <div className="absolute top-4 bottom-4 right-4 z-10 w-56 flex flex-col items-stretch gap-2">
            {/* Undo/redo/save bar */}
            <div className="flex flex-row w-full gap-1">
              <div className="flex-1 flex items-center justify-between border rounded-full bg-white shadow-xs p-1.5">
                <div className="flex items-center gap-1">
                  <div className="w-5 h-5 rounded bg-gray-100 animate-pulse" />
                  <div className="w-5 h-5 rounded bg-gray-100 animate-pulse" />
                </div>
                <div className="w-12 h-5 rounded bg-gray-100 animate-pulse" />
              </div>
              <div className="flex items-center border rounded-full bg-white shadow-xs p-1.5">
                <div className="w-5 h-5 rounded bg-gray-100 animate-pulse" />
              </div>
            </div>
            {/* Properties panel */}
            <div className="flex-1 bg-white rounded-lg border border-gray-200 shadow-sm p-4">
              <div className="w-24 h-3 rounded bg-gray-100 animate-pulse mb-4" />
              <div className="space-y-3">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="h-8 rounded bg-gray-50 animate-pulse" />
                ))}
              </div>
            </div>
          </div>

          {/* Pricing skeleton — bottom left */}
          <div className="absolute bottom-4 left-[284px] z-10 w-56">
            <div className="border bg-gray-50 rounded-2xl px-2.5 py-1.5">
              <div className="flex items-center justify-between">
                <div className="w-16 h-3 rounded bg-gray-200 animate-pulse" />
                <div className="w-20 h-3 rounded bg-gray-200 animate-pulse" />
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
